import styled from '@emotion/styled';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Skeleton, Typography } from '@sunrun/experience-ui-components';
import { tokens } from '@sunrun/experience-ui-components';
import { RecruiterStars } from 'components/atoms/RecruiterStars';
import { VerticalWidgetScrollerContainer } from 'components/atoms/VerticalWidgetScrollerContainer';
import { DailySnapshot } from 'components/molecules/DailySnapshot';
import { PerformanceSummary } from 'components/molecules/PerformanceSummary';
import { RunXStatus } from 'components/molecules/RunXStatus';
import {
  usePerformanceData,
  useRepProfileData,
} from 'hooks/usePerformanceData';
import {
  getPerformanceScopesForRole,
  resolvePerformanceScopeSelection,
  type PerformanceProfileData,
  type PerformanceScopeKey,
} from 'types/performance';
import { NewDashboardOrgType } from 'helpers/repTypeHelper';

type PerformanceWidgetProps = {
  orgType: NewDashboardOrgType;
};

const recruiterRankToFilledCount = (rank: string) => {
  if (!rank) return 0;
  const m = rank.match(/(\d+)/);
  if (!m) return 0;
  return parseInt(m[1], 10);
};

const RecruitingStars = ({
  profileData,
}: {
  profileData: PerformanceProfileData | null;
}) => {
  const thisQuarterRank = profileData?.RecruiterStatus?.CurrentRank ?? '0 Star';
  const lastQuarterRank =
    profileData?.RecruiterStatus?.PreviousRank ?? '0 Star';

  return (
    <RecruitingStarsSection>
      <Typography>Recruiter Stars</Typography>
      <RecruitingStarsColumns>
        <RecruitingStarsColumn>
          <Typography size="small" fontWeight="medium">
            This quarter
          </Typography>
          <RecruiterStars
            periodLabel="This quarter"
            filledCount={recruiterRankToFilledCount(thisQuarterRank)}
          />
        </RecruitingStarsColumn>
        <RecruitingStarsColumn>
          <Typography size="small" fontWeight="medium">
            Last quarter
          </Typography>
          <RecruiterStars
            periodLabel="Last quarter"
            filledCount={recruiterRankToFilledCount(lastQuarterRank)}
          />
        </RecruitingStarsColumn>
      </RecruitingStarsColumns>
    </RecruitingStarsSection>
  );
};

const PerformanceWidget = ({ orgType }: PerformanceWidgetProps) => {
  const { performanceData, isPerformanceDataLoading, performanceDataError } =
    usePerformanceData();
  const { profileData } = useRepProfileData();

  const allowedScopes = useMemo((): PerformanceScopeKey[] => {
    const role = performanceData?.user_config?.role;
    if (!role) {
      return [];
    }
    return getPerformanceScopesForRole(role);
  }, [performanceData]);

  const resolvedDefaultScope = useMemo((): PerformanceScopeKey => {
    if (!performanceData?.user_config) {
      return 'me';
    }
    return resolvePerformanceScopeSelection(
      performanceData.user_config,
      allowedScopes,
    );
  }, [performanceData, allowedScopes]);

  const isManager = resolvedDefaultScope !== 'me';

  const [scopeOverride, setScopeOverride] =
    useState<PerformanceScopeKey | null>(null);

  useEffect(() => {
    if (!performanceData) {
      return;
    }
    setScopeOverride((prev) => {
      if (prev === null) {
        return null;
      }
      if (!allowedScopes.includes(prev)) {
        return null;
      }
      return prev;
    });
  }, [allowedScopes, performanceData, performanceData?.user_config]);

  const scope = scopeOverride ?? resolvedDefaultScope;

  const runXStatus = performanceData && profileData && (
    <Fragment key="runx-status">
      <Divider />
      <RunXStatus profileData={profileData} orgType={orgType} />
    </Fragment>
  );

  const recruitingStars =
    performanceData && profileData?.RecruiterStatus != null ? (
      <Fragment key="recruiting-stars">
        <Divider />
        <RecruitingStars profileData={profileData} />
      </Fragment>
    ) : null;

  let widgetSections = [
    <Divider key="divider1" />,
    performanceData && (
      <DailySnapshot
        data={performanceData}
        allowedScopes={allowedScopes}
        scope={scope}
        onScopeChange={setScopeOverride}
        orgType={orgType}
        key="daily-snapshot"
      />
    ),
    <Divider key="divider2" />,
    performanceData && (
      <PerformanceSummary
        data={performanceData}
        orgType={orgType}
        key="performance-summary"
      />
    ),
  ];

  if (isManager) {
    widgetSections.push(runXStatus);
    widgetSections.push(recruitingStars);
  } else {
    widgetSections = [runXStatus, recruitingStars, ...widgetSections];
  }

  return (
    <Container>
      <VerticalWidgetScrollerContainer>
        {isPerformanceDataLoading && (
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <StyledSkeleton key={i} />
            ))}
          </>
        )}
        {!isPerformanceDataLoading && performanceData && widgetSections}
        {!isPerformanceDataLoading && !performanceData && (
          <EmptyState>
            <Typography size="small">
              {performanceDataError
                ? 'Unable to load scorecard data.'
                : 'Scorecard data is not available.'}
            </Typography>
          </EmptyState>
        )}
      </VerticalWidgetScrollerContainer>
    </Container>
  );
};

const Divider = styled.div`
  flex-shrink: 0;
  height: 1px;
  background-color: ${tokens.TINTS_OFF_WHITE_40};
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  min-height: 64px;
  border-radius: 12px;
`;

const EmptyState = styled.div`
  padding: 12px 0;
`;

const RecruitingStarsSection = styled.section`
  padding: 14px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RecruitingStarsColumns = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 32px;
  align-items: flex-start;
`;

const RecruitingStarsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export { PerformanceWidget };
